/* A bankroll's balance is not in the public API — it states the pool's address
   and token and stops. The figure is on chain: the ERC-20 `balanceOf` of that
   address on the token contract, which is exactly what the whitelabel reads
   (its useEvmTokenBalance → wagmi useBalance). No wallet is involved: this is a
   public read against the chain's public RPC, so it works signed out and on the
   server.

   Only chains with a known public RPC can be read; an unlisted one yields null
   and the tile shows the token without a figure rather than a guessed one. */

const RPC_URLS: Record<number, string> = {
  1: "https://ethereum-rpc.publicnode.com",
  137: "https://polygon-bor-rpc.publicnode.com",
  42793: "https://node.mainnet.etherlink.com",
  128123: "https://node.ghostnet.etherlink.com",
  127823: "https://node.shadownet.etherlink.com",
  8453: "https://mainnet.base.org",
  84532: "https://sepolia.base.org",
  42161: "https://arb1.arbitrum.io/rpc",
  421614: "https://sepolia-rollup.arbitrum.io/rpc",
  10: "https://mainnet.optimism.io",
  11155420: "https://sepolia.optimism.io",
  81457: "https://rpc.blast.io",
  168587773: "https://sepolia.blast.io",
};

const BALANCE_OF = "0x70a08231";

const padAddress = (address: string) => address.toLowerCase().replace(/^0x/, "").padStart(64, "0");

/* The ERC-20 balance of `holder` in the token's base units, or null when the
   chain has no known RPC or the node cannot be reached — a balance is never
   invented, only read. */
export const erc20BalanceOf = async ({
  chainId,
  tokenAddress,
  holder,
  signal,
}: {
  chainId: number | undefined;
  tokenAddress: string;
  holder: string;
  signal?: AbortSignal;
}): Promise<bigint | null> => {
  if (chainId === undefined) return null;

  const rpc = RPC_URLS[chainId];

  if (!rpc || !/^0x[0-9a-fA-F]{40}$/.test(tokenAddress) || !/^0x[0-9a-fA-F]{40}$/.test(holder)) {
    return null;
  }

  try {
    const response = await fetch(rpc, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_call",
        params: [{ to: tokenAddress, data: BALANCE_OF + padAddress(holder) }, "latest"],
      }),
      signal,
      cache: "no-store",
    });

    if (!response.ok) return null;

    const body = (await response.json()) as { result?: string; error?: unknown };

    if (typeof body.result !== "string" || body.result === "0x") return null;

    return BigInt(body.result);
  } catch {
    return null;
  }
};

/* The same read through a wallet's own EIP-1193 provider — for a chain with no
   public RPC here, or one that did not answer. The wallet can only speak for
   the chain it is on; the caller checks that before asking. */
export const erc20BalanceOfVia = async (
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>,
  tokenAddress: string,
  holder: string,
): Promise<bigint | null> => {
  if (!/^0x[0-9a-fA-F]{40}$/.test(tokenAddress) || !/^0x[0-9a-fA-F]{40}$/.test(holder)) {
    return null;
  }

  try {
    const result = await request({
      method: "eth_call",
      params: [{ to: tokenAddress, data: BALANCE_OF + padAddress(holder) }, "latest"],
    });

    if (typeof result !== "string" || result === "0x") return null;

    return BigInt(result);
  } catch {
    return null;
  }
};

export const hasChainRpc = (chainId: number | undefined): boolean =>
  chainId !== undefined && chainId in RPC_URLS;
